import React, { useEffect, useState, useCallback } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Swal from "sweetalert2";
import Header from "../../components/protected/Header";
import Footer from "../../components/footer";
import { auth } from "../../config/firebase";
import { useTranslations } from "../../translations";
import { useIsAdmin } from "../../hooks/useIsAdmin";

const API_BASE = (
  process.env.REACT_APP_BLOOMS_API_URL || "https://ai-blooms.vercel.app/api/openai/blooms"
).replace(/\/api\/.*/, "");

const genPassword = () => {
  const bytes = new Uint8Array(9);
  window.crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, "").slice(0, 12);
};

const AdminSchools = () => {
  const { t } = useTranslations();
  const { isAdmin, loading: adminLoading } = useIsAdmin();

  const [schools, setSchools] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState(genPassword());
  const [country, setCountry] = useState("VE");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [interval, setInterval] = useState("month");
  const [validUntil, setValidUntil] = useState("");

  const [detail, setDetail] = useState(null); // { school, subscription, counts }
  const [detailLoading, setDetailLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [renewDate, setRenewDate] = useState("");

  const authHeaders = useCallback(async () => {
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken(true) : null;
    return { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` };
  }, []);

  const loadSchools = useCallback(async () => {
    setListLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/api/admin/schools`, { headers });
      const data = await res.json();
      setSchools(data.schools || []);
    } catch (e) {
      /* noop */
    } finally {
      setListLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!adminLoading && isAdmin) loadSchools();
  }, [adminLoading, isAdmin, loadSchools]);

  const createSchool = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const headers = await authHeaders();
      const body = {
        name: name.trim(),
        adminEmail: adminEmail.trim(),
        adminPassword,
        country,
      };
      if (amount) {
        body.customPrice = { amount: Number(amount), currency, interval };
        body.plan = "school_custom";
      }
      if (validUntil) body.currentPeriodEnd = new Date(validUntil).getTime();

      const res = await fetch(`${API_BASE}/api/admin/schools`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error");

      Swal.fire({
        icon: "success",
        title: t("schoolCreated"),
        html: `<div style="text-align:left"><b>${name}</b><br/>${adminEmail}<br/>${t("adminPasswordLabel")}: <code>${adminPassword}</code></div>`,
      });
      setName("");
      setAdminEmail("");
      setAdminPassword(genPassword());
      setAmount("");
      setValidUntil("");
      loadSchools();
    } catch (err) {
      Swal.fire({ icon: "error", title: t("errorTitle"), text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (schoolId) => {
    setDetailLoading(true);
    setDetail({ open: true });
    try {
      const h = await authHeaders();
      const res = await fetch(`${API_BASE}/api/admin/schools/manage?schoolId=${schoolId}`, { headers: h });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error");
      setDetail({ open: true, ...data });
      setRenewDate("");
    } catch (err) {
      Swal.fire({ icon: "error", title: t("errorTitle"), text: err.message });
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const doAction = async (action, extra = {}) => {
    if (action === "suspend") {
      const c = await Swal.fire({ title: t("suspendConfirm"), showCancelButton: true, icon: "warning" });
      if (!c.isConfirmed) return;
    }
    try {
      setActing(true);
      const h = await authHeaders();
      const res = await fetch(`${API_BASE}/api/admin/schools/manage`, {
        method: "POST",
        headers: h,
        body: JSON.stringify({ schoolId: detail.school.id, action, ...extra }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error");
      if (action === "reset_password") {
        Swal.fire({ icon: "success", title: t("newPasswordGenerated"), html: `<code>${data.password}</code>` });
      } else {
        Swal.fire({
          icon: "success",
          title: action === "suspend" ? t("schoolSuspended") : action === "reactivate" ? t("schoolReactivated") : t("schoolRenewed"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
      await openDetail(detail.school.id);
      loadSchools();
    } catch (err) {
      Swal.fire({ icon: "error", title: t("errorTitle"), text: err.message });
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="dashboard-wrapper">
      <Header />
      <main className="dashboard-main-content pb-5">
        <Container fluid className="py-4 mx-auto" style={{ maxWidth: 960 }}>
          <h2 className="mb-4">{t("adminSchoolsTitle")}</h2>

          {adminLoading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : !isAdmin ? (
            <Card className="text-center p-4 border-0 shadow-sm" style={{ borderRadius: 16 }}>
              <Card.Body className="text-muted">{t("notAuthorized")}</Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {/* Formulario de creación */}
              <Col lg={6}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: 16 }}>
                  <Card.Body className="p-4">
                    <h5 className="mb-3">{t("newSchool")}</h5>
                    <Form onSubmit={createSchool}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("schoolName")}</Form.Label>
                        <Form.Control value={name} onChange={(e) => setName(e.target.value)} required />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("adminEmailLabel")}</Form.Label>
                        <Form.Control type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} required />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("adminPasswordLabel")}</Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} required />
                          <button type="button" className="sch-btn sch-btn-ghost" onClick={() => setAdminPassword(genPassword())}>
                            {t("generate")}
                          </button>
                        </div>
                      </Form.Group>
                      <Row>
                        <Col xs={12} md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t("countryLabel")}</Form.Label>
                            <Form.Control value={country} onChange={(e) => setCountry(e.target.value)} />
                          </Form.Group>
                        </Col>
                      </Row>

                      <hr />
                      <div className="text-muted small text-uppercase mb-2" style={{ letterSpacing: 1 }}>
                        {t("institutionalPlan")}
                      </div>
                      <Row>
                        <Col xs={6} md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t("priceAmount")}</Form.Label>
                            <Form.Control type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
                          </Form.Group>
                        </Col>
                        <Col xs={6} md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t("currency")}</Form.Label>
                            <Form.Control value={currency} onChange={(e) => setCurrency(e.target.value)} />
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t("interval")}</Form.Label>
                            <Form.Select value={interval} onChange={(e) => setInterval(e.target.value)}>
                              <option value="month">{t("planMonthly")}</option>
                              <option value="year">{t("planAnnual")}</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t("validUntil")}</Form.Label>
                            <Form.Control type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
                          </Form.Group>
                        </Col>
                      </Row>

                      <button type="submit" className="sch-btn sch-btn-primary w-100" disabled={saving}>
                        {saving ? <Spinner animation="border" size="sm" /> : t("createSchool")}
                      </button>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>

              {/* Listado */}
              <Col lg={6}>
                <h5 className="mb-3">{t("existingSchools")}</h5>
                {listLoading ? (
                  <div className="text-center py-4"><Spinner animation="border" /></div>
                ) : schools.length === 0 ? (
                  <Card className="border-0 shadow-sm text-muted p-4" style={{ borderRadius: 16 }}>
                    {t("noSchools")}
                  </Card>
                ) : (
                  schools.map((s) => (
                    <Card
                      key={s.id}
                      className="border-0 shadow-sm mb-3 sch-card"
                      style={{ borderRadius: 14, cursor: "pointer" }}
                      onClick={() => openDetail(s.id)}
                    >
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div>
                            <div className="fw-semibold">{s.name}</div>
                            <div className="small text-muted">{s.contactEmail}{s.country ? ` · ${s.country}` : ""}</div>
                          </div>
                          <div className="text-end">
                            <Badge bg={s.status === "active" ? "success" : "secondary"}>{s.status}</Badge>
                            <div className="small text-primary mt-1">{t("manageSchool")} ›</div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))
                )}
              </Col>
            </Row>
          )}
        </Container>
        <Footer logged={true} />
      </main>

      <Modal show={!!detail} onHide={() => setDetail(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.15rem" }}>
            {detail && detail.school ? detail.school.name : "…"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading || !detail || !detail.school ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <>
              <div className="mb-3">
                <div className="small text-muted">{detail.school.contactEmail}</div>
                <Badge bg={detail.school.status === "active" ? "success" : "secondary"}>
                  {detail.school.status}
                </Badge>
              </div>
              <Row className="text-center mb-3 g-2">
                <Col xs={4}><div className="fw-bold fs-5">{detail.counts?.teachers ?? 0}</div><div className="small text-muted">{t("teachers")}</div></Col>
                <Col xs={4}><div className="fw-bold fs-5">{detail.counts?.sections ?? 0}</div><div className="small text-muted">{t("sections")}</div></Col>
                <Col xs={4}><div className="fw-bold fs-5">{detail.counts?.students ?? 0}</div><div className="small text-muted">{t("students")}</div></Col>
              </Row>
              <div className="mb-3 p-3" style={{ background: "#f6f6f8", borderRadius: 10 }}>
                <div className="small text-muted">{t("subscriptionLabel")}</div>
                {detail.subscription ? (
                  <div>
                    <span className="fw-semibold text-capitalize">{detail.subscription.status}</span>
                    {detail.subscription.currentPeriodEnd
                      ? ` · ${t("validUntil")} ${new Date(detail.subscription.currentPeriodEnd).toLocaleDateString()}`
                      : ""}
                  </div>
                ) : (
                  <div className="text-muted">—</div>
                )}
              </div>

              <div className="d-flex flex-wrap gap-2 mb-3">
                {detail.school.status === "active" ? (
                  <button className="sch-btn sch-btn-ghost" disabled={acting} onClick={() => doAction("suspend")}>
                    {t("suspend")}
                  </button>
                ) : (
                  <button className="sch-btn sch-btn-primary" disabled={acting} onClick={() => doAction("reactivate")}>
                    {t("reactivate")}
                  </button>
                )}
                <button className="sch-btn sch-btn-ghost" disabled={acting} onClick={() => doAction("reset_password")}>
                  {t("resetPassword")}
                </button>
              </div>

              <div className="d-flex gap-2 align-items-end">
                <Form.Group className="flex-grow-1">
                  <Form.Label className="small">{t("renew")} — {t("validUntil")}</Form.Label>
                  <Form.Control type="date" value={renewDate} onChange={(e) => setRenewDate(e.target.value)} />
                </Form.Group>
                <button
                  className="sch-btn sch-btn-primary"
                  disabled={acting || !renewDate}
                  onClick={() => doAction("renew", { currentPeriodEnd: new Date(renewDate).getTime() })}
                >
                  {t("renew")}
                </button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <style>{`
        .sch-btn {
          display:inline-flex; align-items:center; justify-content:center;
          height:auto !important; width:auto !important;
          padding:10px 18px; border-radius:10px; font-size:.9rem; font-weight:600;
          border:1.5px solid transparent; cursor:pointer; transition:filter .15s ease;
        }
        .sch-btn:disabled { opacity:.6; cursor:default; }
        .sch-btn-primary { background:linear-gradient(135deg,#3d1a33,#eb2178); color:#fff; }
        .sch-btn-primary:hover:not(:disabled) { filter:brightness(1.08); }
        .sch-btn-ghost { background:#f4f4f6; color:#444; white-space:nowrap; }
        .sch-btn-ghost:hover { background:#e9e9ee; }
      `}</style>
    </div>
  );
};

export default AdminSchools;
